<template>
  <div>
    <div class="adm-toolbar">
      <p class="adm-muted adm-admins-intro">Admins can approve doctors and manage clinics, logins and cities.</p>
      <div class="adm-toolbar__spacer"></div>
      <button type="button" class="adm-btn adm-btn--primary" @click="openCreate">
        <i class="fas fa-user-shield"></i> Add admin
      </button>
    </div>

    <div class="adm-card">
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead>
            <tr>
              <th>Admin</th>
              <th>Last sign-in</th>
              <th>Status</th>
              <th class="adm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="n in 2" :key="`s${n}`">
                <td v-for="c in 3" :key="c"><div class="adm-skeleton" style="width: 60%"></div></td>
                <td></td>
              </tr>
            </template>
            <template v-else>
              <tr v-for="admin in admins" :key="admin.id">
                <td>
                  <div class="adm-person">
                    <span class="adm-avatar adm-tone-violet">{{ initials(admin.name) }}</span>
                    <span>
                      <span class="adm-person__name">
                        {{ admin.name }}
                        <span v-if="isMe(admin)" class="adm-badge adm-badge--info adm-badge--plain">You</span>
                      </span>
                      <span class="adm-person__meta">{{ admin.email }}</span>
                    </span>
                  </div>
                </td>
                <td class="adm-muted adm-nowrap">{{ admin.has_login ? formatDateTime(admin.last_login) : "—" }}</td>
                <td>
                  <span v-if="!admin.has_login" class="adm-badge adm-badge--pending" title="Signs in through the old Cognito login until moved">Not moved yet</span>
                  <span v-else class="adm-badge" :class="admin.is_active ? 'adm-badge--active' : 'adm-badge--inactive'">
                    {{ admin.is_active ? "Active" : "Disabled" }}
                  </span>
                </td>
                <td class="adm-col-actions">
                  <button type="button" class="adm-btn adm-btn--sm" @click="resetting = { admin, password: '', error: '' }">
                    {{ admin.has_login ? "Reset password" : "Set password" }}
                  </button>
                  <button v-if="!isMe(admin)" type="button" class="adm-btn adm-btn--sm"
                          @click="admin.is_active ? (confirm = { admin, action: 'disable' }) : setActive(admin, true)">
                    {{ admin.is_active ? "Disable" : "Enable" }}
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create -->
    <admin-modal v-if="creating" title="Add admin" subtitle="The login works straight away with the password you set."
                 :busy="saving" @close="creating = false">
      <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>
      <div class="adm-field">
        <label class="adm-label" for="adm-name">Full name <span class="adm-req">*</span></label>
        <input id="adm-name" v-model="form.name" class="adm-input" :class="{ 'is-invalid': errors.name }" maxlength="100" />
        <div v-if="errors.name" class="adm-field__error">{{ errors.name }}</div>
      </div>
      <div class="adm-field">
        <label class="adm-label" for="adm-email">Email <span class="adm-req">*</span></label>
        <input id="adm-email" v-model="form.email" type="email" class="adm-input" :class="{ 'is-invalid': errors.email }" maxlength="150" />
        <div v-if="errors.email" class="adm-field__error">{{ errors.email }}</div>
      </div>
      <password-field v-model="form.password" :error="errors.password" />
      <div class="adm-alert adm-alert--warning">
        <i class="fas fa-shield-alt"></i>
        <div>Admins have full access to doctors, clinics and logins. Only add people you trust.</div>
      </div>
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="creating = false">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="create">
          <span v-if="saving" class="adm-spinner"></span> Create admin
        </button>
      </template>
    </admin-modal>

    <!-- Set a new password -->
    <admin-modal v-if="resetting" :title="`${resetting.admin.has_login ? 'New password' : 'Password'} for ${resetting.admin.name}`"
                 :subtitle="resetting.admin.has_login ? 'They are signed out everywhere and must use the new password.' : 'Gives this admin a login in the new system.'"
                 :busy="saving" @close="resetting = null">
      <password-field v-model="resetting.password" label="New password" :error="resetting.error" />
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="resetting = null">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="resetPassword">
          <span v-if="saving" class="adm-spinner"></span> Set password
        </button>
      </template>
    </admin-modal>

    <!-- Confirm disable -->
    <admin-modal v-if="confirm" size="sm" :busy="saving" title="Disable admin?" @close="confirm = null">
      <p class="adm-confirm-text">
        <strong>{{ confirm.admin.name }}</strong> will be signed out and lose admin access until you enable them again.
      </p>
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="confirm = null">Cancel</button>
        <button type="button" class="adm-btn adm-btn--danger" :disabled="saving" @click="setActive(confirm.admin, false)">
          <span v-if="saving" class="adm-spinner"></span> Disable
        </button>
      </template>
    </admin-modal>
  </div>
</template>

<script>
import axios from "axios";
import AdminModal from "./AdminModal.vue";
import PasswordField, { passwordError } from "./PasswordField.vue";
import { EMAIL_RE, errorMessage, formatDateTime, initials } from "../admin-utils";

export default {
  name: "AdminsPanel",
  // The shell passes every panel the same props; ignore the ones this panel does not use.
  inheritAttrs: false,
  components: { AdminModal, PasswordField },
  props: {
    currentUserId: { type: String, default: "" },
    intent: { type: Object, default: null },
  },
  emits: ["notify", "changed"],
  data() {
    return {
      admins: [],
      loading: false,
      saving: false,
      creating: false,
      confirm: null,
      resetting: null,
      form: { name: "", email: "", password: "" },
      errors: {},
      formError: "",
    };
  },
  watch: {
    // A stale "enter a password" error should go once one is typed or generated.
    "form.password"() {
      if (this.errors.password) this.errors = { ...this.errors, password: "" };
    },
    "resetting.password"() {
      if (this.resetting) this.resetting.error = "";
    },
  },
  methods: {
    initials,
    formatDateTime,
    isMe(admin) {
      return !!this.currentUserId && admin.login_id === this.currentUserId;
    },
    async fetchAdmins() {
      this.loading = true;
      try {
        const { data } = await axios.get("/admin/admins");
        this.admins = data || [];
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not load admins."), "error");
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.form = { name: "", email: "", password: "" };
      this.errors = {};
      this.formError = "";
      this.creating = true;
    },
    async create() {
      const errors = {};
      if (!this.form.name.trim()) errors.name = "Enter the admin's name.";
      if (!EMAIL_RE.test(this.form.email.trim())) errors.email = "Enter a valid email.";
      if (passwordError(this.form.password)) errors.password = passwordError(this.form.password);
      this.errors = errors;
      if (Object.keys(errors).length) return;
      this.saving = true;
      this.formError = "";
      try {
        const { data } = await axios.post("/admin/admins", {
          name: this.form.name.trim(),
          email: this.form.email.trim(),
          password: this.form.password,
        });
        this.$emit("notify", `Admin added. ${data.email} can sign in now with the password you set.`);
        this.creating = false;
        this.$emit("changed");
        await this.fetchAdmins();
      } catch (error) {
        this.formError = errorMessage(error, "Could not add the admin.");
      } finally {
        this.saving = false;
      }
    },
    async resetPassword() {
      const { admin, password } = this.resetting;
      this.resetting.error = passwordError(password);
      if (this.resetting.error) return;
      this.saving = true;
      try {
        const { data } = await axios.post(`/admin/admins/${admin.id}/reset-password`, { password });
        this.$emit("notify", `Password set for ${data.email}.`);
        this.resetting = null;
        await this.fetchAdmins();
      } catch (error) {
        this.resetting.error = errorMessage(error, "Could not set the password.");
      } finally {
        this.saving = false;
      }
    },
    async setActive(admin, active) {
      this.saving = true;
      try {
        await axios.patch(`/admin/admins/${admin.id}/status`, { is_active: active });
        this.$emit("notify", `${admin.name} was ${active ? "enabled" : "disabled"}.`);
        this.confirm = null;
        await this.fetchAdmins();
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not update the admin."), "error");
      } finally {
        this.saving = false;
      }
    },
  },
  created() {
    this.fetchAdmins();
    if (this.intent && this.intent.create) this.openCreate();
  },
};
</script>

<style scoped>
.adm-admins-intro {
  margin: 0;
}

.adm-confirm-text {
  margin: 0;
}
</style>
